import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { startPlacementQuiz, answerPlacementQuiz, QuizCard } from '../api/quiz';
import { useAuth } from '../context/AuthContext';
import AnswerButton from '../components/AnswerButton';
import '../styles/global.css';

type FeedbackState = 'default' | 'correct' | 'wrong';

const PlacementQuiz: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [showIntro, setShowIntro] = useState(true);
  const [quizId, setQuizId] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [totalBudget, setTotalBudget] = useState(20);
  const [card, setCard] = useState<QuizCard | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [feedbackState, setFeedbackState] = useState<FeedbackState>('default');
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [result, setResult] = useState<{
    placement_category_name: string;
    categories_remaining: number;
    message: string;
  } | null>(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const startQuiz = useCallback(async () => {
    setShowIntro(false);
    setIsLoading(true);
    try {
      const data = await startPlacementQuiz();
      setQuizId(data.quiz_id);
      setTotalBudget(data.total_budget);
      setCurrentQuestion(data.current_question);
      setCard(data.card);
      setCategoryName(data.category_name);
    } catch (err: any) {
      if (err.response?.status === 409) {
        navigate('/account', { replace: true });
        return;
      }
      setError(err.response?.data?.detail || 'Failed to start quiz');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const handleAnswer = useCallback(async (answer: string) => {
    if (isSubmitting || feedbackState !== 'default') return;

    setIsSubmitting(true);
    setSelectedAnswer(answer);

    try {
      const data = await answerPlacementQuiz(quizId, answer);

      // Show feedback
      setFeedbackState(data.correct ? 'correct' : 'wrong');

      // Wait 500ms then advance
      setTimeout(() => {
        if (data.quiz_complete) {
          setIsComplete(true);
          setResult({
            placement_category_name: data.placement_category_name || '',
            categories_remaining: data.categories_remaining || 0,
            message: data.message || '',
          });
        } else {
          setCard(data.next_card || null);
          setCategoryName(data.next_category_name || '');
          setCurrentQuestion(data.current_question + 1);
          setFeedbackState('default');
          setSelectedAnswer(null);
        }
        setIsSubmitting(false);
      }, 500);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit answer');
      setIsSubmitting(false);
      setFeedbackState('default');
      setSelectedAnswer(null);
    }
  }, [quizId, isSubmitting, feedbackState]);

  const handleFinish = async () => {
    await refreshUser();
    navigate('/account', { replace: true });
  };

  // Intro screen
  if (showIntro) {
    return (
      <div className="page">
        <div className="container" style={{ textAlign: 'center', paddingTop: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#129504;</div>
          <h2 style={{ marginBottom: 12 }}>Quick Placement Quiz</h2>
          <p style={{ fontSize: 16, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
            Before you start playing, we need to figure out the right level for you!
          </p>
          <div className="card" style={{ textAlign: 'left', padding: 20, marginBottom: 24 }}>
            <p style={{ marginBottom: 12, fontWeight: 'bold' }}>How it works:</p>
            <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8, color: 'var(--text-secondary)' }}>
              <li>You'll answer up to 20 quick math questions</li>
              <li>Questions will get harder or easier based on your answers</li>
              <li>Don't worry about getting some wrong — that helps us find your level!</li>
              <li>There's no timer, so take your time</li>
            </ul>
          </div>
          <button className="btn btn-primary" onClick={startQuiz} style={{ width: '100%', fontSize: 18 }}>
            I'm ready!
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="page">
        <div className="game-loading">
          <p>Preparing your placement quiz...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="page">
        <div className="container">
          <div className="error">{error}</div>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
            style={{ marginTop: 20 }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Results screen
  if (isComplete && result) {
    return (
      <div className="page">
        <div className="container" style={{ textAlign: 'center', paddingTop: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 20 }}>&#127942;</div>
          <h2 style={{ marginBottom: 10 }}>Quiz Complete!</h2>
          <p style={{ fontSize: 18, color: 'var(--text-secondary)', marginBottom: 30 }}>
            {result.message}
          </p>
          <div className="card" style={{ marginBottom: 30, padding: 20 }}>
            <p style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 8 }}>
              You're starting at:
            </p>
            <p style={{ fontSize: 20, color: 'var(--primary-color)' }}>
              {result.placement_category_name}
            </p>
            <p style={{ color: 'var(--text-secondary)', marginTop: 8 }}>
              {result.categories_remaining} categories to master
            </p>
          </div>
          <button className="btn btn-primary" onClick={handleFinish} style={{ width: '100%' }}>
            Let's go!
          </button>
        </div>
      </div>
    );
  }

  // Quiz in progress
  if (!card) {
    return (
      <div className="page">
        <div className="container">
          <div className="error">No question available</div>
        </div>
      </div>
    );
  }

  const progressPercent = (currentQuestion / totalBudget) * 100;
  const labels = ['A', 'B', 'C', 'D'];

  const getAnswerState = (answer: string): 'default' | 'correct' | 'wrong' => {
    if (feedbackState === 'default') return 'default';
    if (answer === selectedAnswer) return feedbackState;
    return 'default';
  };

  return (
    <div className="page game-page">
      <div className="game-header">
        <div className="game-progress">
          Question {currentQuestion} of {totalBudget}
        </div>
      </div>

      <div className="game-container">
        {/* Progress bar */}
        <div style={{
          width: '100%',
          height: 8,
          backgroundColor: 'var(--border-color)',
          borderRadius: 4,
          marginBottom: 16,
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${progressPercent}%`,
            height: '100%',
            backgroundColor: 'var(--primary-color)',
            borderRadius: 4,
            transition: 'width 0.3s ease',
          }} />
        </div>

        {/* Category being tested */}
        <div className="game-category">
          Testing: {categoryName}
        </div>

        {/* Question */}
        <div className="question-card">
          <div className="question-text">{card.question}</div>
        </div>

        {/* Answer options */}
        <div className="answers-grid">
          {card.options.map((option, index) => (
            <AnswerButton
              key={`${currentQuestion}-${index}`}
              label={labels[index]}
              answer={option}
              onClick={handleAnswer}
              disabled={isSubmitting || feedbackState !== 'default'}
              state={getAnswerState(option)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlacementQuiz;
